import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { FileText, Upload, CheckCircle2 } from "lucide-react";

const STATUS_COLORS = {
  submitted: "bg-amber-100 text-amber-900 border-amber-200",
  under_review: "bg-blue-100 text-blue-900 border-blue-200",
  approved: "bg-emerald-100 text-emerald-900 border-emerald-200",
  rejected: "bg-rose-100 text-rose-900 border-rose-200",
  active: "bg-indigo-100 text-indigo-900 border-indigo-200",
  completed: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_LABEL = {
  submitted: "Submitted", under_review: "Under Review", approved: "Approved",
  rejected: "Rejected", active: "Active", completed: "Completed",
};

export default function LeasingPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [mine, setMine] = useState([]);
  const [form, setForm] = useState({
    desired_vehicle: "",
    vehicle_id: "",
    term_months: 12,
    monthly_budget: "",
    employment_status: "Employed",
    employer: "",
    monthly_income: "",
    notes: "",
    document_paths: [],
  });
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then(r => setVehicles(r.data)).catch(() => {});
    if (user) api.get("/leasing/mine").then(r => setMine(r.data)).catch(() => {});
  }, [user]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error("Sign in first"); return nav("/login"); }
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/upload", fd);
      setUploads(arr => [...arr, { name: f.name, path: data.path }]);
      setForm(s => ({ ...s, document_paths: [...(s.document_paths || []), data.path] }));
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast("Sign in"); return nav("/login"); }
    if (!form.desired_vehicle) return toast.error("Select a vehicle");
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_budget: parseFloat(form.monthly_budget || 0),
        monthly_income: parseFloat(form.monthly_income || 0),
        term_months: parseInt(form.term_months, 10),
      };
      await api.post("/leasing/apply", payload);
      toast.success("Application submitted!");
      const { data } = await api.get("/leasing/mine");
      setMine(data);
      setForm({ desired_vehicle: "", vehicle_id: "", term_months: 12, monthly_budget: "",
                employment_status: "Employed", employer: "", monthly_income: "", notes: "",
                document_paths: [] });
      setUploads([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="bg-[#1D4ED8] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#FFCE00]">Vehicle Leasing</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl mt-3">Lease a vehicle, your way.</h1>
          <p className="mt-3 max-w-xl text-white/90">Apply online, upload documents, and we'll review your application within 2 business days.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 text-[#E32636]"><FileText className="h-5 w-5" strokeWidth={2.5} /><span className="tag-overline !text-[#E32636]">Apply now</span></div>
          <h2 className="font-heading font-black text-2xl mt-1 text-slate-900">Lease application</h2>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-sm font-bold text-slate-900">Desired vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => {
                const sel = vehicles.find(x => x.id === v);
                setForm(s => ({ ...s, vehicle_id: v, desired_vehicle: sel ? `${sel.year} ${sel.make} ${sel.model} (${sel.color})` : v }));
              }}>
                <SelectTrigger data-testid="lease-vehicle"><SelectValue placeholder="Pick a vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.color}</SelectItem>)}
                  <SelectItem value="other">Other (specify in notes)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Term (months)</Label>
                <Input type="number" min={6} max={60} data-testid="lease-term" value={form.term_months} onChange={e => setForm({ ...form, term_months: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Monthly budget (EC$)</Label>
                <Input type="number" data-testid="lease-budget" value={form.monthly_budget} onChange={e => setForm({ ...form, monthly_budget: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900">Employment status</Label>
              <Select value={form.employment_status} onValueChange={(v) => setForm({ ...form, employment_status: v })}>
                <SelectTrigger data-testid="lease-employment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Employed", "Self-Employed", "Business Owner", "Contractor", "Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-bold text-slate-900">Employer / Business</Label>
                <Input data-testid="lease-employer" value={form.employer} onChange={e => setForm({ ...form, employer: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm font-bold text-slate-900">Monthly income (EC$)</Label>
                <Input type="number" data-testid="lease-income" value={form.monthly_income} onChange={e => setForm({ ...form, monthly_income: e.target.value })} />
              </div>
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900">Supporting documents (ID, payslips, bank statements…)</Label>
              <label className="mt-1 flex items-center justify-center h-24 w-full border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:border-[#1D4ED8] bg-slate-50">
                <input type="file" accept="image/*,application/pdf" onChange={upload} className="hidden" data-testid="lease-doc-upload" />
                <div className="text-center text-sm text-slate-600">
                  <Upload className="h-5 w-5 mx-auto text-slate-400" />
                  {uploads.length ? `${uploads.length} file(s) uploaded — click to add more` : "Click to upload (PDF / image)"}
                </div>
              </label>
              {uploads.length > 0 && (
                <ul className="mt-2 text-xs text-slate-600 space-y-1">
                  {uploads.map((u, i) => <li key={i} className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> {u.name}</li>)}
                </ul>
              )}
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900">Notes</Label>
              <Textarea data-testid="lease-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>

            <Button type="submit" disabled={busy} data-testid="lease-submit-btn"
                    className="w-full h-12 bg-[#E32636] hover:bg-[#C91F2C] text-white font-bold">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-heading font-black text-xl text-slate-900">Your applications</h3>
          {mine.length === 0 && <p className="text-sm text-slate-500">You haven't applied yet.</p>}
          {mine.map(l => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`lease-${l.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold text-slate-900">{l.desired_vehicle}</div>
                <span className={`text-xs font-bold uppercase tracking-wider border rounded-md px-2 py-1 ${STATUS_COLORS[l.status] || ""}`}>
                  {STATUS_LABEL[l.status] || l.status}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-1">{l.term_months} months · Budget EC${l.monthly_budget || 0}</div>
              {l.monthly_payment && (
                <div className="text-xs text-slate-700 mt-1">Approved payment: <b>EC${l.monthly_payment}/mo</b></div>
              )}
              {l.admin_notes && <div className="text-xs text-slate-600 mt-2 italic">"{l.admin_notes}"</div>}
              {(l.payments || []).length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="font-bold text-slate-700">Payments ({l.payments.length})</div>
                  {l.payments.map(p => (
                    <div key={p.id} className="text-slate-600">EC${p.amount} · {p.method} · {p.received_at?.slice(0,10)}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
}
